import {
  ArrowPathIcon,
  EyeSlashIcon,
  FolderArrowDownIcon,
  MinusCircleIcon,
  PencilIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { ReactNode } from 'react';
import { EmptyState } from '../EmptyState';
import { SkeletonTable } from '../Skeleton';
import { ActionRow, HeaderRow } from './TableHeaderRow';
import { TableRow } from './TableRow';

export enum TableActions {
  edit = 'edit',
  delete = 'delete',
  add = 'add',
  download = 'download',
  deactivate = 'deactivate',
  cancel = 'cancel',
  relist = 'relist',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic column type with any default for flexibility
export type Column<T = any> = {
  label: string;
  cyTag: string;
  cySortTag?: string;
  data: string | ((row: RowData<T>, index: number) => ReactNode);
  formatter?: (data: string) => string;
  // if sortKey is not provided, the column will not be sortable
  sortKey?: string;
};

interface RowRequiredProps {
  id?: number | null;
  keyPrefix?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic row data type with any default for flexibility
type RowData<T = any> = RowRequiredProps & {
  [K in keyof T]: T[K];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic type parameter with any default for flexibility
interface TableProps<T = any> {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic tags object for dynamic data attributes
  tags: { [key: string]: any };
  rowData: RowData<T>[];
  columns: Column<T>[];
  loading: boolean;
  /**
   * [MCV-1587] Either one action set for the whole table (the original shape,
   * and still what every existing caller passes) or a per-row function.
   *
   * The per-row form exists because "deactivate" is only meaningful on a row
   * that is currently active -- offering it on an already-inactive listing is
   * an action that silently does nothing. A table-wide array cannot express
   * that, and widening to a union leaves every array caller untouched.
   */
  actions?: TableActions[] | ((row: RowData<T>) => TableActions[]);
  onSort?: (sortBy: string) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number, rowIdx?: number) => void;
  onDownload?: (id: number, rowIdx?: number) => void;
  onDeactivate?: (id: number, rowIdx?: number) => void;
  onCancel?: (id: number, rowIdx?: number) => void;
  onRelist?: (id: number, rowIdx?: number) => void;
}
export const Table = (props: TableProps) => {
  const {
    tags,
    columns,
    rowData,
    onSort,
    onEdit,
    onDelete,
    onDownload,
    onDeactivate,
    onCancel,
    onRelist,
    loading,
    actions,
  } = props;

  return (
    <table className="text-grey-100 w-full">
      <thead>
        <tr className="px-3">
          {columns.map((col, idx) => {
            const { label, cySortTag, cyTag, sortKey } = col;
            return (
              <HeaderRow
                key={(cySortTag || cyTag) + idx}
                label={label}
                sortKey={sortKey}
                cyTag={cySortTag || cyTag}
                onSort={onSort}
              />
            );
          })}
          {actions && <ActionRow />}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={12}>
              <SkeletonTable rows={4} columns={columns.length} />
            </td>
          </tr>
        ) : !rowData.length ? (
          <tr>
            <td colSpan={12}>
              <EmptyState />
            </td>
          </tr>
        ) : (
          rowData.map((row, rowIdx) => {
            // [MCV-1587] Resolve the per-row action set once. `actions` stays
            // truthy in either form, so the `{actions && (` guard below is
            // unchanged for the callers that pass a plain array.
            const rowActions = typeof actions === 'function' ? actions(row) : actions;
            return (
              <tr
                key={row.keyPrefix ? `${row.keyPrefix} - ${row.id}` : row.id}
                data-cy={tags.row + rowIdx}
                className="text-left border-b border-grey-900 hover:bg-primary-t-950 transition-colors"
              >
                {columns.map((col, colIdx) => {
                  const { cyTag, data, formatter } = col;
                  return (
                    <TableRow
                      key={cyTag + rowIdx}
                      cyTag={cyTag + rowIdx}
                      className={rowIdx === 0 ? 'pt-4' : ''}
                    >
                      {typeof data === 'string'
                        ? formatter
                          ? formatter(row[data])
                          : row[data]
                        : data(row, rowIdx)}
                    </TableRow>
                  );
                })}
                {/* [MCV-1849] The action cell is right-aligned to match the Actions header,
                    so rows with fewer icons (an inactive listing has no Deactivate) line up
                    on the same right edge instead of ragged at the left. */}
                {rowActions && (
                  <td className={`relative py-3 text-right ${rowIdx === 0 ? 'pt-4' : ''}`}>
                    <div className="flex items-center justify-end gap-2">
                      {rowActions.includes(TableActions.edit) && (
                        <button
                          type="button"
                          aria-label="Edit"
                          title="Edit"
                          data-cy={tags.editBtn + rowIdx}
                          onClick={() => row.id && onEdit?.(row.id)}
                          className="rounded-md p-1.5 text-grey-400 hover:text-primary-50 hover:bg-primary-t-900 transition-colors cursor-pointer"
                        >
                          <PencilIcon className="size-4 shrink-0" aria-hidden="true" />
                        </button>
                      )}
                      {rowActions.includes(TableActions.deactivate) && (
                        <button
                          type="button"
                          aria-label="Deactivate"
                          title="Deactivate"
                          data-cy={tags.deactivateBtn + rowIdx}
                          onClick={() => row.id && onDeactivate?.(row.id, rowIdx)}
                          className="rounded-md p-1.5 text-grey-400 hover:text-primary-50 hover:bg-primary-t-900 transition-colors cursor-pointer"
                        >
                          <EyeSlashIcon className="size-4 shrink-0" aria-hidden="true" />
                        </button>
                      )}
                      {rowActions.includes(TableActions.cancel) && (
                        <button
                          type="button"
                          aria-label="Cancel listing"
                          title="Cancel listing"
                          data-cy={'cancelBtn' + rowIdx}
                          onClick={() => row.id && onCancel?.(row.id, rowIdx)}
                          className="rounded-md p-1.5 text-grey-400 hover:text-error-500 hover:bg-error-50/10 transition-colors cursor-pointer"
                        >
                          <XCircleIcon className="size-4 shrink-0" aria-hidden="true" />
                        </button>
                      )}
                      {rowActions.includes(TableActions.relist) && (
                        <button
                          type="button"
                          aria-label="Relist as a new draft"
                          title="Relist as a new draft"
                          data-cy={'relistBtn' + rowIdx}
                          onClick={() => row.id && onRelist?.(row.id, rowIdx)}
                          className="rounded-md p-1.5 text-grey-400 hover:text-primary-50 hover:bg-primary-t-900 transition-colors cursor-pointer"
                        >
                          <ArrowPathIcon className="size-4 shrink-0" aria-hidden="true" />
                        </button>
                      )}
                      {rowActions.includes(TableActions.delete) && (
                        <button
                          type="button"
                          aria-label="Delete"
                          title="Delete"
                          data-cy={tags.deleteBtn + rowIdx}
                          onClick={() => row.id && onDelete?.(row.id, rowIdx)}
                          className="rounded-md p-1.5 text-grey-400 hover:text-error-500 hover:bg-error-50/10 transition-colors cursor-pointer"
                        >
                          <MinusCircleIcon className="size-4 shrink-0" aria-hidden="true" />
                        </button>
                      )}
                      {rowActions.includes(TableActions.download) && (
                        <button
                          type="button"
                          aria-label="Download"
                          title="Download"
                          data-cy={tags.downloadBtn + rowIdx}
                          onClick={() => row.id && onDownload?.(row.id, rowIdx)}
                          className="rounded-md p-1.5 text-grey-400 hover:text-primary-50 hover:bg-primary-t-900 transition-colors cursor-pointer"
                        >
                          <FolderArrowDownIcon className="size-4 shrink-0" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};
